import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts';
import { SupplyShedDriveTime } from '../../../lib/types';
import { usePerTreeRollup } from '../../../hooks/usePerTreeRollup';
import { shortTons } from '../../../lib/units';
import { StatCard } from '../layers/StatCard';
import {
  CHART_ACCENT_COLOR,
  CHART_AXIS_STROKE,
  CHART_AXIS_STYLE,
  CHART_PRIMARY_COLOR,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '../../../lib/chartStyles';
import { InsightProvenanceFooter } from './InsightProvenanceFooter';

interface Props {
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
}

const formatCount = (n: number): string => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toFixed(0);
};

const formatTonsCompact = (mg: number): string => {
  const t = shortTons(mg);
  if (t >= 1e6) return `${(t / 1e6).toFixed(2)}M`;
  if (t >= 1e3) return `${(t / 1e3).toFixed(1)}K`;
  return t.toFixed(0);
};

export const PerTreeCarbonRollupCard: React.FC<Props> = ({ millCode, driveTime }) => {
  const { data, isLoading, error } = usePerTreeRollup(millCode, driveTime);

  if (error && !data) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-4 border border-red-500/40 shadow-figma-card">
        <p className="text-xs font-roboto-mono text-red-400 uppercase tracking-wider">
          Insight unavailable
        </p>
        <p className="mt-2 text-body-sm font-roboto text-neutral-400">{error}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
        <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
          Per-Tree Carbon Rollup
        </p>
        <p className="mt-3 text-body-sm font-roboto text-neutral-400">Loading…</p>
      </div>
    );
  }

  // Bottom-up AGB-only vs top-down AGB — the apples-to-apples agreement bar.
  // Both quantities are above-ground biomass, in short tons. The comparison
  // is gated on `topDownAgbMg !== null`: when the precompute had only
  // partial NAIP coverage of the shed, the server skipped the comparison
  // (the AGB CSV is shed-wide-average density, which mis-states the integral
  // over a partial footprint) and the card hides the bar + the two
  // top-down stat cells, surfacing a coverage caveat instead.
  const showComparison = data.topDownAgbMg !== null && data.topDownAgbMg > 0;
  const coverageFraction =
    data.shedTotalAcres > 0 ? data.coveredAcres / data.shedTotalAcres : 0;
  const comparisonData = showComparison
    ? [
        { label: 'Bottom-up', total: shortTons(data.aboveGroundBiomassMg), color: CHART_ACCENT_COLOR },
        { label: 'Top-down 100m', total: shortTons(data.topDownAgbMg as number), color: CHART_PRIMARY_COLOR },
      ]
    : [];

  return (
    <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card space-y-4">
      <div>
        <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
          Insight — Per-Tree Carbon Rollup
        </p>
        <p className="mt-3 text-4xl font-roboto-mono font-bold text-figma-yellow leading-none">
          {formatCount(data.treeCount)}
        </p>
        <p className="mt-1 text-body-sm font-roboto text-neutral-300">
          individual trees underwriting the shed-wide carbon claim
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Total Tree Biomass"
          value={`${formatTonsCompact(data.totalTreeBiomassMg)} tons`}
          subtitle="Includes ~18% coarse-root mass"
          info="Sum across crowns of total NSVB biomass (5 above-ground components + coarse root). Displayed in US short tons."
        />
        <StatCard
          label="Bottom-up AGB"
          value={`${formatTonsCompact(data.aboveGroundBiomassMg)} tons`}
          subtitle="Coarse root excluded; comparable to satellite AGB"
          info="Crown-level above-ground biomass (coarse root subtracted out). Apples-to-apples with the AGB 100m satellite layer — the cross-validation that backs the shed-wide carbon claim."
        />
        {showComparison ? (
          <>
            <StatCard
              label="Top-down AGB"
              value={`${formatTonsCompact(data.topDownAgbMg as number)} tons`}
              info="AGB 100m raster integrated across the supply shed."
            />
            <StatCard
              label="Agreement"
              value={`${data.agreementPct.toFixed(1)}%`}
              info="Bottom-up AGB ÷ top-down AGB. Two independent estimators of the same physical quantity, agreeing — the auditable basis of the carbon claim."
            />
          </>
        ) : (
          <StatCard
            label="Sawtimber trees"
            value={formatCount(data.sawtimberCount)}
            info="Crowns with estimated DBH ≥ 22.9 cm. Top-down satellite comparison is suppressed until pilot NAIP coverage spans the full shed (currently partial)."
          />
        )}
      </div>

      {showComparison ? (
        // Bottom-up vs top-down AGB — the "are we agreeing?" bar. Both bars are
        // AGB-only (root excluded server-side), so the gap visualised here is
        // methodology disagreement, not the root-vs-no-root accounting choice.
        <div>
          <p className="text-xs font-roboto text-neutral-500 mb-1">
            Carbon claim · bottom-up cross-validates top-down (short tons)
          </p>
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={comparisonData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <XAxis
                  dataKey="label"
                  tick={{ ...CHART_AXIS_STYLE, fill: CHART_AXIS_STROKE }}
                  axisLine={{ stroke: CHART_AXIS_STROKE }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ ...CHART_AXIS_STYLE, fill: CHART_AXIS_STROKE }}
                  axisLine={{ stroke: CHART_AXIS_STROKE }}
                  tickLine={false}
                  width={48}
                  tickFormatter={(v: number) => `${(v / 1e6).toFixed(1)}M`}
                />
                <Tooltip
                  cursor={false}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                  formatter={(value: number | undefined) => [
                    typeof value === 'number' ? `${(value / 1e6).toFixed(2)}M tons` : '—',
                    'AGB',
                  ]}
                />
                <Bar dataKey="total">
                  {comparisonData.map((d) => (
                    <Cell key={d.label} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="rounded border border-figma-yellow/40 bg-figma-yellow/5 p-3">
          <p className="text-[11px] font-roboto-mono text-figma-yellow uppercase tracking-wider">
            Top-down comparison · partial coverage
          </p>
          <p className="mt-1 text-xs font-roboto text-neutral-300 leading-relaxed">
            Pilot NAIP coverage spans {(coverageFraction * 100).toFixed(1)}% of the
            {' '}{Math.round(data.shedTotalAcres).toLocaleString()}-acre shed; the
            shed-wide AGB-layer average can&rsquo;t be honestly multiplied over a
            partial footprint. The agreement bar will appear once the remaining
            tiles are processed.
          </p>
        </div>
      )}

      {/* Diameter-class histogram. Class buckets follow FIA convention; DBH
          itself is biased low (saturates ≈32–44 cm even for 60+ m trees), so
          the large-saw bar is a conservative floor. */}
      <div>
        <p className="text-xs font-roboto text-neutral-500 mb-1">
          Tree count by diameter class
        </p>
        <div className="h-24">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <BarChart data={data.diameterClasses} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <XAxis
                dataKey="label"
                tick={{ ...CHART_AXIS_STYLE, fill: CHART_AXIS_STROKE }}
                axisLine={{ stroke: CHART_AXIS_STROKE }}
                tickLine={false}
              />
              <YAxis
                tick={{ ...CHART_AXIS_STYLE, fill: CHART_AXIS_STROKE }}
                axisLine={{ stroke: CHART_AXIS_STROKE }}
                tickLine={false}
                width={44}
                tickFormatter={(v: number) => formatCount(v)}
              />
              <Tooltip
                cursor={false}
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                formatter={(value: number | undefined) => [
                  typeof value === 'number' ? formatCount(value) : '—',
                  'Trees',
                ]}
              />
              <Bar dataKey="count" fill={CHART_PRIMARY_COLOR} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <InsightProvenanceFooter
        chips={[
          { color: CHART_ACCENT_COLOR, label: 'Tree-Level Attributes (CTrees / TreeMap / NSVB)' },
          { color: CHART_PRIMARY_COLOR, label: 'AGB 100m' },
        ]}
        method="Bottom-up: NSVB allometric coefficients applied to NAIP-derived crowns and CHM heights, with the coarse-root component subtracted to isolate AGB. Top-down: AGB 100m raster integrated across the supply shed. Agreement = bottom-up AGB ÷ top-down AGB. Total Tree Biomass (above-ground + coarse root) reported separately."
        stubLabel={data.source === 'stub' ? 'Pilot preview — data in production pipeline' : undefined}
      />
    </div>
  );
};
